<template>
  <div class="mapeamento-job-page">
    <nav class="page-breadcrumb mb-1 d-flex align-items-center text-muted small">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Configurações</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <router-link :to="{ name: 'jobs-erp' }" class="text-muted">Jobs</router-link>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold text-body">Mapeamento</span>
    </nav>

    <b-card class="mb-1" v-if="job">
      <b-row>
        <b-col md="8">
          <h5 class="mb-25">{{ job.nome }}</h5>
          <div class="text-muted small">{{ job.descricao || '—' }}</div>
        </b-col>
        <b-col md="4" class="text-right">
          <div class="small text-muted">Integração</div>
          <strong>{{ job.integracao_nome || '—' }}</strong>
          <div class="small text-muted">{{ job.integracao_metodo }} · {{ job.cron_expression || 'sem cron' }}</div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Status do mapeamento existente -->
    <b-card v-if="mapeamentoAtual" class="mb-1 border-success">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <b-badge variant="light-success" class="mr-50">
            <feather-icon icon="CheckIcon" size="12" /> Mapeamento configurado
          </b-badge>
          <strong>{{ mapeamentoAtual.tabela_alvo }}</strong>
          <span class="text-muted small ml-50">
            · {{ Object.keys(mapeamentoAtual.mapeamento || {}).length }} campos
            · PK: {{ (mapeamentoAtual.pk_colunas || []).join(', ') || '—' }}
            · Modo: {{ mapeamentoAtual.modo }}
          </span>
        </div>
        <b-button size="sm" variant="outline-danger" @click="confirmarExcluir">
          <feather-icon icon="Trash2Icon" size="13" /> Remover mapeamento
        </b-button>
      </div>
    </b-card>

    <!-- Passo 1: Análise da resposta da API -->
    <b-card class="mb-1">
      <h6 class="mb-1">
        <b-badge variant="primary" class="mr-25">1</b-badge>
        Analisar resposta da API
      </h6>

      <b-row class="align-items-end mb-1">
        <b-col md="6">
          <label class="small text-muted">Caminho dos dados <span class="text-muted">(opcional)</span></label>
          <b-form-input
            v-model="caminhoDados"
            size="sm"
            placeholder='Ex.: "data.items" — deixe vazio para auto-detectar'
          />
        </b-col>
        <b-col md="6" class="text-right">
          <b-button size="sm" variant="primary" :disabled="analisando" @click="sugerir">
            <b-spinner small v-if="analisando" />
            <feather-icon v-else icon="SearchIcon" size="13" />
            Analisar última execução e sugerir tabela
          </b-button>
        </b-col>
      </b-row>

      <b-alert show variant="warning" v-if="erroSugerir">
        {{ erroSugerir }}
      </b-alert>

      <div v-if="analise" class="analise-resumo bg-light rounded p-1">
        <div class="row">
          <div class="col-md-4">
            <div class="small text-muted">Caminho detectado</div>
            <code>{{ analise.caminho_detectado || '(raiz)' }}</code>
          </div>
          <div class="col-md-4">
            <div class="small text-muted">Registros encontrados</div>
            <strong>{{ analise.total_registros }}</strong>
          </div>
          <div class="col-md-4">
            <div class="small text-muted">Campos por registro</div>
            <strong>{{ (analise.campos_api || []).length }}</strong>
          </div>
        </div>
        <div class="mt-50" v-if="analise.campos_api && analise.campos_api.length">
          <div class="small text-muted mb-25">Campos detectados</div>
          <b-badge v-for="c in analise.campos_api" :key="c" variant="light-info" class="mr-25 mb-25">
            {{ c }}
          </b-badge>
        </div>
      </div>
    </b-card>

    <!-- Passo 2: Escolher tabela alvo -->
    <b-card class="mb-1" v-if="analise && analise.sugestoes">
      <h6 class="mb-1">
        <b-badge variant="primary" class="mr-25">2</b-badge>
        Escolher tabela alvo
      </h6>

      <div v-if="!analise.sugestoes.length" class="text-muted py-2 text-center">
        Nenhuma tabela do banco apresentou similaridade com os campos detectados.
        Você pode informar a tabela manualmente abaixo.
      </div>

      <div class="table-grid">
        <div
          v-for="s in analise.sugestoes"
          :key="s.tabela"
          class="table-card"
          :class="{ active: tabelaSelecionada === s.tabela }"
          @click="selecionarTabela(s.tabela)"
        >
          <div class="d-flex align-items-center justify-content-between">
            <strong>{{ s.tabela }}</strong>
            <b-badge :variant="s.score >= 70 ? 'light-success' : s.score >= 40 ? 'light-warning' : 'light-secondary'">
              {{ s.score }}%
            </b-badge>
          </div>
          <div class="small text-muted">
            {{ s.casados }}/{{ s.total_campos_api }} campos casam · ~{{ s.linhas_aprox || 0 }} linhas
          </div>
          <div class="small mt-25" v-if="s.tem_pk">
            <feather-icon icon="KeyIcon" size="11" /> PK: {{ (s.pk_sugerida || []).join(', ') }}
          </div>
          <div class="small mt-25 text-warning" v-else>
            <feather-icon icon="AlertTriangleIcon" size="11" /> Sem PK — UPSERT não funciona
          </div>
        </div>
      </div>

      <div class="mt-1">
        <label class="small text-muted">Ou informe manualmente:</label>
        <div class="d-flex" style="gap:6px">
          <b-form-input v-model="tabelaManual" size="sm" placeholder="nome_da_tabela" />
          <b-button size="sm" variant="outline-primary" @click="aplicarTabelaManual" :disabled="!tabelaManual">
            Usar
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Passo 3: Configurar mapeamento -->
    <b-card v-if="tabelaSelecionada && colunasTabela.length">
      <h6 class="mb-1">
        <b-badge variant="primary" class="mr-25">3</b-badge>
        Mapear campos → colunas de <code>{{ tabelaSelecionada }}</code>
      </h6>

      <b-row class="mb-1">
        <b-col md="6">
          <b-form-group label="Chave para UPSERT (PK / UNIQUE)">
            <v-select-fallback
              v-model="pkColunas"
              :options="colunasTabela.map((c) => c.nome)"
              multiple
              placeholder="Selecione 1 ou mais colunas"
            />
            <small class="text-muted">
              A tabela precisa ter PK ou UNIQUE INDEX sobre estas colunas para evitar duplicidade.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Modo de aplicação">
            <b-form-select v-model="modo" size="sm">
              <b-form-select-option value="UPSERT">UPSERT (insere/atualiza)</b-form-select-option>
              <b-form-select-option value="APPEND">APPEND (só inserir)</b-form-select-option>
              <b-form-select-option value="REPLACE">REPLACE (limpa tabela antes)</b-form-select-option>
              <b-form-select-option value="PED_CAPA">Retorno de pedido — situações da capa</b-form-select-option>
              <b-form-select-option value="PED_ITENS">Retorno de pedido — situação dos itens</b-form-select-option>
              <b-form-select-option value="PED_BO">Retorno de pedido — atendimento e BOs</b-form-select-option>
            </b-form-select>
            <small v-if="modo.startsWith('PED_')" class="text-muted d-block mt-1">
              Retorno protegido: localiza o pedido pela referência ERP, sem criar pedidos.
              Lê situações dentro de sls_pedidoitens. Capa: PD4000 → Sales e Mac → Comercial.
              Itens: Mac → situação comercial, somente quando a linha é identificada sem ambiguidade.
              Atendimento/BOs: preserva a referência e os dados de cada etapa.
              Não sobrescreve emissão, tipo, cliente, quantidades ou valores da compra original.
              O descritor NumeroPedidoOriginal identifica o vínculo; não converte a referência diretamente em ID.
              Os três modos da mesma integração compartilham uma única resposta salva.
              Agende somente o coletor; use Reprocessar no histórico para repetir sem consultar o ERP.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Status">
            <b-form-checkbox v-model="ativo" switch>
              {{ ativo ? 'Ativo' : 'Inativo' }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex align-items-center mb-50">
        <strong class="mr-auto">Mapeamento</strong>
        <b-button size="sm" variant="outline-primary" @click="executarAutomap" :disabled="!camposApi.length">
          <feather-icon icon="ZapIcon" size="13" /> Auto-mapear por similaridade
        </b-button>
      </div>

      <b-table small responsive :items="linhasMapeamento" :fields="fieldsMapeamento">
        <template #cell(campo_api)="{ item }">
          <code>{{ item.campo_api }}</code>
        </template>
        <template #cell(coluna_db)="{ item }">
          <b-form-select
            :value="mapeamento[item.campo_api] || null"
            size="sm"
            @change="(v) => setColunaMapeada(item.campo_api, v)"
          >
            <b-form-select-option :value="null">— ignorar este campo —</b-form-select-option>
            <b-form-select-option v-for="c in colunasTabela" :key="c.nome" :value="c.nome">
              {{ c.nome }} <span v-if="c.pk">(PK)</span>
              <span v-else-if="c.unica">(UNQ)</span>
            </b-form-select-option>
          </b-form-select>
        </template>
        <template #cell(tipo)="{ item }">
          <span class="small text-muted" v-if="mapeamento[item.campo_api]">
            {{ tipoColuna(mapeamento[item.campo_api]) }}
          </span>
        </template>
        <template #cell(exemplo)="{ item }">
          <span class="small text-muted">{{ exemploCampo(item.campo_api) }}</span>
        </template>
      </b-table>

      <div class="mt-1 pt-1 border-top">
        <div class="d-flex align-items-center mb-50">
          <strong class="mr-auto">Preenchimento de chave (fallback)</strong>
          <b-button size="sm" variant="outline-primary" @click="addFallback">
            <feather-icon icon="PlusIcon" size="13" /> Adicionar regra
          </b-button>
        </div>
        <p class="small text-muted mb-50">
          Quando a coluna chegar vazia/zero no payload, é preenchida com outro campo da API.
          Ex.: a API manda <code>clienteId = 0</code>, mas o id real está em <code>clienteIdERP</code>.
        </p>
        <div
          v-for="(f, i) in fallbackList"
          :key="`fb-${i}`"
          class="d-flex align-items-center mb-50"
          style="gap:6px"
        >
          <b-form-select v-model="f.coluna" size="sm" style="max-width:240px">
            <b-form-select-option value="">— coluna alvo —</b-form-select-option>
            <b-form-select-option v-for="c in colunasTabela" :key="c.nome" :value="c.nome">
              {{ c.nome }}
            </b-form-select-option>
          </b-form-select>
          <feather-icon icon="ArrowLeftIcon" size="14" class="text-muted" />
          <b-form-select v-model="f.campo" size="sm" style="max-width:240px">
            <b-form-select-option value="">— campo da API —</b-form-select-option>
            <b-form-select-option v-for="campo in camposApi" :key="campo" :value="campo">
              {{ campo }}
            </b-form-select-option>
          </b-form-select>
          <b-button size="sm" variant="flat-danger" @click="removeFallback(i)" title="Remover regra">
            <feather-icon icon="XIcon" size="13" />
          </b-button>
        </div>
        <div v-if="!fallbackList.length" class="small text-muted">Nenhuma regra de fallback.</div>
      </div>

      <div class="d-flex justify-content-end mt-1" style="gap:6px">
        <b-button variant="outline-secondary" :to="{ name: 'jobs-erp' }">Cancelar</b-button>
        <b-button variant="primary" :disabled="salvando" @click="salvar">
          <b-spinner small v-if="salvando" />
          <feather-icon v-else icon="SaveIcon" size="13" />
          Salvar mapeamento
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import JobErpClient from '@/api/Integracao/jobErp.client';
import JobMapeamentoClient from '@/api/Integracao/jobMapeamento.client';
import IntegracaoSchemaClient from '@/api/Integracao/integracaoSchema.client';

const jobClient = new JobErpClient();
const mapClient = new JobMapeamentoClient();
const schemaClient = new IntegracaoSchemaClient();

/**
 * Componente substituto para v-select (mantém compat com Vue 2 + bootstrap-vue).
 * Apenas para multi-select de colunas-chave; usa <select multiple> nativo.
 */
const VSelectFallback = {
  props: { value: { type: Array, default: () => [] }, options: { type: Array, default: () => [] } },
  render(h) {
    return h('select', {
      attrs: { multiple: true, class: 'form-control form-control-sm', size: Math.min(6, this.options.length) },
      on: {
        change: (e) => {
          const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
          this.$emit('input', selected);
        },
      },
    }, this.options.map((opt) => h('option', {
      attrs: { value: opt, selected: this.value.includes(opt) },
    }, opt)));
  },
};

export default {
  name: 'MapeamentoJob',
  components: { 'v-select-fallback': VSelectFallback },
  data() {
    return {
      job: null,
      mapeamentoAtual: null,

      caminhoDados: '',
      analisando: false,
      analise: null,
      erroSugerir: null,

      tabelaSelecionada: null,
      tabelaManual: '',
      colunasTabela: [],
      camposApi: [],
      mapeamento: {},
      pkColunas: [],
      modo: 'UPSERT',
      ativo: true,
      fallbackList: [],
      salvando: false,

      fieldsMapeamento: [
        { key: 'campo_api', label: 'Campo da API' },
        { key: 'coluna_db', label: 'Coluna do banco' },
        { key: 'tipo',      label: 'Tipo' },
        { key: 'exemplo',   label: 'Exemplo' },
      ],
    };
  },
  computed: {
    // Apenas leitura: lista de linhas para a b-table. A edição não percorre
    // este array — chamadas vão direto em setColunaMapeada() via @change,
    // evitando watcher reativo que causa loop infinito sobre `mapeamento`.
    linhasMapeamento() {
      return this.camposApi.map((c) => ({ campo_api: c }));
    },
  },
  async created() {
    await Promise.all([this.carregarJob(), this.carregarMapeamentoExistente()]);
    if (this.mapeamentoAtual) {
      this.caminhoDados = this.mapeamentoAtual.caminho_dados || '';
      this.modo = this.mapeamentoAtual.modo || 'UPSERT';
      this.ativo = this.mapeamentoAtual.ativo !== false;
      this.pkColunas = this.mapeamentoAtual.pk_colunas || [];
      this.mapeamento = { ...(this.mapeamentoAtual.mapeamento || {}) };
      this.fallbackList = Object.entries(this.mapeamentoAtual.fallback_chave || {})
        .map(([coluna, campo]) => ({ coluna, campo }));
      this.camposApi = Object.keys(this.mapeamento);
      if (this.mapeamentoAtual.tabela_alvo) {
        await this.selecionarTabela(this.mapeamentoAtual.tabela_alvo, { skipAutomap: true });
      }
    }
  },
  methods: {
    async carregarJob() {
      try {
        const { data } = await jobClient.buscarPorId(this.$route.params.id);
        this.job = data && data.data ? data.data : null;
      } catch (e) { this.job = null; }
    },
    async carregarMapeamentoExistente() {
      try {
        const { data } = await mapClient.obter(this.$route.params.id);
        this.mapeamentoAtual = data && data.data ? data.data : null;
      } catch (e) { this.mapeamentoAtual = null; }
    },
    async sugerir() {
      this.analisando = true;
      this.erroSugerir = null;
      try {
        const body = {};
        if (this.caminhoDados) body.caminho_dados = this.caminhoDados;
        const { data } = await mapClient.sugerir(this.$route.params.id, body);
        this.analise = data && data.data ? data.data : null;
        this.camposApi = (this.analise && this.analise.campos_api) || [];
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || e.message || String(e);
        this.erroSugerir = msg;
      } finally {
        this.analisando = false;
      }
    },
    async selecionarTabela(tabela, { skipAutomap = false } = {}) {
      this.tabelaSelecionada = tabela;
      try {
        const { data } = await schemaClient.listarColunas(tabela);
        this.colunasTabela = (data && data.data && data.data.colunas) || [];
      } catch (e) {
        this.colunasTabela = [];
        this.$bvToast.toast(`Falha ao carregar colunas de ${tabela}`, { title: 'Erro', variant: 'danger', solid: true });
        return;
      }
      if (!skipAutomap && this.camposApi.length) {
        await this.executarAutomap();
      }
      // Se PK ainda não selecionada, sugere via meta
      if (!this.pkColunas.length) {
        const pks = this.colunasTabela.filter((c) => c.pk).map((c) => c.nome);
        if (pks.length) this.pkColunas = pks;
      }
    },
    setColunaMapeada(campoApi, coluna) {
      // Vue.set / $set garante reatividade ao adicionar/remover chaves do objeto
      if (coluna) this.$set(this.mapeamento, campoApi, coluna);
      else        this.$delete(this.mapeamento, campoApi);
    },
    addFallback() {
      this.fallbackList.push({ coluna: '', campo: '' });
    },
    removeFallback(i) {
      this.fallbackList.splice(i, 1);
    },
    buildFallbackChave() {
      const out = {};
      this.fallbackList.forEach((f) => {
        if (f.coluna && f.campo) out[f.coluna] = f.campo;
      });
      return out;
    },
    async aplicarTabelaManual() {
      if (this.tabelaManual) await this.selecionarTabela(this.tabelaManual.trim());
    },
    async executarAutomap() {
      if (!this.tabelaSelecionada || !this.camposApi.length) return;
      try {
        const { data } = await mapClient.automapear(this.$route.params.id, {
          tabela: this.tabelaSelecionada,
          campos_api: this.camposApi,
        });
        const res = data && data.data;
        if (res) {
          this.mapeamento = { ...(res.mapeamento || {}) };
          if (!this.pkColunas.length && (res.pk_sugerida || []).length) {
            this.pkColunas = res.pk_sugerida;
          }
        }
      } catch (e) {
        this.$bvToast.toast(`Falha ao auto-mapear: ${e.message || e}`, { title: 'Erro', variant: 'danger', solid: true });
      }
    },
    tipoColuna(nome) {
      const c = this.colunasTabela.find((x) => x.nome === nome);
      return c ? c.tipo_completo : '';
    },
    exemploCampo(campo) {
      if (!this.analise || !this.analise.exemplo) return '';
      const v = this.analise.exemplo[campo];
      if (v === null || v === undefined) return '(nulo)';
      if (typeof v === 'object') return JSON.stringify(v).slice(0, 60);
      const s = String(v);
      return s.length > 50 ? `${s.slice(0, 50)}…` : s;
    },
    async salvar() {
      if (!this.tabelaSelecionada) {
        this.$bvToast.toast('Selecione uma tabela alvo.', { title: 'Atenção', variant: 'warning', solid: true });
        return;
      }
      if (!this.pkColunas.length) {
        this.$bvToast.toast('Selecione ao menos uma coluna como chave.', { title: 'Atenção', variant: 'warning', solid: true });
        return;
      }
      if (!Object.keys(this.mapeamento).length) {
        this.$bvToast.toast('Mapeie ao menos um campo.', { title: 'Atenção', variant: 'warning', solid: true });
        return;
      }
      this.salvando = true;
      try {
        const payload = {
          tabela_alvo: this.tabelaSelecionada,
          pk_colunas: this.pkColunas,
          mapeamento: this.mapeamento,
          caminho_dados: this.caminhoDados || null,
          modo: this.modo,
          ativo: this.ativo,
          fallback_chave: this.buildFallbackChave(),
        };
        const { data } = await mapClient.salvar(this.$route.params.id, payload);
        this.mapeamentoAtual = data && data.data ? data.data : null;
        this.$bvToast.toast('Mapeamento salvo. Próximas execuções já vão aplicar.',
          { title: 'OK', variant: 'success', solid: true });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || e.message || String(e);
        this.$bvToast.toast(`Falha ao salvar: ${msg}`, { title: 'Erro', variant: 'danger', solid: true });
      } finally {
        this.salvando = false;
      }
    },
    async confirmarExcluir() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Remover o mapeamento deste job? As próximas execuções voltarão apenas a salvar o payload bruto.',
        { title: 'Confirmar', okTitle: 'Remover', okVariant: 'danger', cancelTitle: 'Cancelar', centered: true },
      );
      if (!ok) return;
      try {
        await mapClient.excluir(this.$route.params.id);
        this.mapeamentoAtual = null;
        this.tabelaSelecionada = null;
        this.colunasTabela = [];
        this.mapeamento = {};
        this.pkColunas = [];
        this.$bvToast.toast('Mapeamento removido.', { title: 'OK', variant: 'success', solid: true });
      } catch (e) {
        this.$bvToast.toast(`Falha ao remover: ${e.message || e}`, { title: 'Erro', variant: 'danger', solid: true });
      }
    },
  },
};
</script>

<style scoped>
.page-breadcrumb a:hover { color: var(--primary); }

.table-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}
.table-card {
  border: 1px solid var(--border-color, rgba(128, 128, 128, 0.2));
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
}
.table-card:hover {
  border-color: var(--primary);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.table-card.active {
  border-color: var(--success, #28c76f);
  box-shadow: 0 1px 4px rgba(40, 199, 111, 0.18);
}
</style>
